import type { ReactNode } from 'react';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import type { Conversation } from '@/features/AIChat/types/chat';
import { analytics } from '@/services/analytics';
import type { RootState } from '@/store';
import { getStagingSuffix } from '@/utils/environmentPaths';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { getGxgoConfigApiUrl } from '@/utils/gxgoConfigUrl';
import { handleThunkError } from '@/utils/thunkErrorHandler';

const CONFIG_NAME = 'userConfig';
export type Option = {
  label: string;
  value: string;
  info?: ReactNode;
};
export type UserConfig = {
  timezone: Option;
  dateFormat: Option;
  defaultMarket: Option;
  homepage: Option;
  chatHistory: Conversation[];
  exportVideoExplainerSeen?: boolean;
};

type UserConfigState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  userConfig: UserConfig;
};

type Request = {
  userConfig: UserConfig;
};

const initialState: UserConfigState = {
  status: 'idle',
  error: null,
  userConfig: {
    timezone: { label: 'UTC', value: 'UTC' },
    dateFormat: { label: '14-Mar-25', value: 'dd-MMM-yy' },
    defaultMarket: { label: '', value: '' },
    homepage: { label: '', value: '' },
    chatHistory: []
  }
};

type UserConfigResponse = Partial<UserConfig> & { defaultInsight?: Option };

const parseUserConfigResponse = (data: UserConfigResponse): UserConfig => {
  const defaultMarket = data.defaultMarket ?? data.defaultInsight ?? { label: '', value: '' };
  const homepage = data.homepage?.value.length ? data.homepage : defaultMarket;
  const chatHistory = data.chatHistory ?? [];
  return {
    timezone: data.timezone ?? { label: 'UTC', value: 'UTC' },
    dateFormat: data.dateFormat ?? { label: '14-Mar-25', value: 'dd-MMM-yy' },
    defaultMarket,
    homepage,
    chatHistory
  };
};

/**
 * Fetches user config from the API with migration support.
 * On staging, if the staging endpoint returns 404, falls back to the
 * non-staging endpoint and migrates the config to staging.
 */
export const fetchUserConfig = async () => {
  try {
    const { data } = await httpInstances.axiosInternalInstance.get(
      `${getGxgoConfigApiUrl()}/userConfig`
    );
    return parseUserConfigResponse(data);
  } catch (error) {
    // If staging endpoint returns 404, try the non-staging endpoint for migration
    // Remove this fallback once all staging users are migrated.
    if (error instanceof AxiosError && error.response?.status === 404) {
      const { data } = await httpInstances.axiosInternalInstance.get('/config/gxgo/userConfig');
      // Migrate user config to staging endpoint
      const configPayload = { [CONFIG_NAME]: data };
      await httpInstances.axiosInternalInstance.post(getGxgoConfigApiUrl(), configPayload);
      return parseUserConfigResponse(data);
    }
    throw error;
  }
};

export const getUserConfig = createAsyncThunk('getUserConfig', async (_, { rejectWithValue }) => {
  try {
    return await fetchUserConfig();
  } catch (error) {
    return handleThunkError(error, rejectWithValue) as never;
  }
});

export const saveUserConfig = createAsyncThunk(
  'saveUserConfig',
  async (request: Request, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const userEmail = state.tripToken.userEmail;
      const previousHomepage = state.userConfig.userConfig.homepage.value;
      const responses = await postUserConfig(request, userEmail ?? '', previousHomepage);

      if (responses.every(r => r.status === 200)) {
        analytics.captureUserProperties({
          defaultMarket: request.userConfig.defaultMarket.label,
          homepage: request.userConfig.homepage.label
        });
        return request.userConfig;
      }
      return rejectWithValue("Couldn't save config file");
    } catch (error) {
      return handleThunkError(error, rejectWithValue) as never;
    }
  }
);
const userConfigSlice = createSlice({
  name: CONFIG_NAME,
  initialState,
  reducers: {
    setExplainerVideoSeen: state => {
      state.userConfig.exportVideoExplainerSeen = true;
    },
    revertExplainerVideoSeen: state => {
      state.userConfig.exportVideoExplainerSeen = false;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getUserConfig.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(getUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error?.message);
      })
      .addCase(saveUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(saveUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error?.message);
      });
  }
});

export const { setExplainerVideoSeen, revertExplainerVideoSeen } = userConfigSlice.actions;
export default userConfigSlice.reducer;

export const markExplainerVideoSeen = createAsyncThunk(
  'markExplainerVideoSeen',
  async (_, { getState, dispatch, rejectWithValue }) => {
    dispatch(setExplainerVideoSeen());
    try {
      const { userConfig } = (getState() as RootState).userConfig;
      await httpInstances.axiosInternalInstance.post(getGxgoConfigApiUrl(), {
        [CONFIG_NAME]: userConfig
      });
    } catch (error) {
      dispatch(revertExplainerVideoSeen());
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      throw error;
    }
  }
);

const postUserConfig = (request: Request, userEmail: string, previousHomepage: string) => {
  const configPayload = { [CONFIG_NAME]: request.userConfig };
  const newHomepage = request.userConfig.homepage.value;

  const postResponsePromise = httpInstances.axiosInternalInstance.post(
    getGxgoConfigApiUrl(),
    configPayload
  );

  if (newHomepage !== previousHomepage) {
    const prefMarket = getStagingSuffix(newHomepage);
    const patchResponsePromise = httpInstances.axiosGoInstance.patch(`/users/${userEmail}`, {
      prefMarket
    });
    return Promise.all([postResponsePromise, patchResponsePromise]);
  }

  return Promise.all([postResponsePromise]);
};
